import { Info } from 'lucide-react'

import type { ArtifactResponse } from '@/client/types.gen'
import { Button as UIButton } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTranslation } from '@/hooks/useTranslation'
import { TOUR } from '@/lib/product-tour/constants'

import { ArtifactCard } from './ArtifactCard'
import type { StepComponentProps } from './types'
import {
	canAddAnalysisSource,
	getCompletedArtifacts,
	getTotalSourceCount,
	MAX_ANALYSIS_SOURCES,
} from './utils'

interface PrimaryFileStepProps extends StepComponentProps {
	artifacts: ArtifactResponse[]
	selectedFiles: ArtifactResponse[]
	contextFiles: ArtifactResponse[]
	onToggle: (artifact: ArtifactResponse) => void
}

export function PrimaryFileStep({
	artifacts,
	selectedFiles = [],
	contextFiles = [],
	onToggle,
	onContinue,
}: PrimaryFileStepProps) {
	const { t } = useTranslation()
	const completedArtifacts = getCompletedArtifacts(artifacts)
	const selectedSourceCount = getTotalSourceCount(selectedFiles, contextFiles)
	const canAddMoreSources = canAddAnalysisSource(selectedFiles, contextFiles)

	return (
		<div className="flex-1 flex flex-col min-h-0 space-y-4">
			<div>
				<div className="flex items-center gap-2 mb-1.5">
					<h2 className="text-lg font-semibold text-foreground">
						{t('pages.projectDetail.modal.step1.title')}
					</h2>
					<Tooltip>
						<TooltipTrigger asChild>
							<Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
						</TooltipTrigger>
						<TooltipContent>
							<p>{t('pages.projectDetail.modal.step1.tooltip')}</p>
						</TooltipContent>
					</Tooltip>
				</div>
				<p className="text-sm text-muted-foreground">
					{t('pages.projectDetail.modal.sourceLimit', {
						count: selectedSourceCount,
						max: MAX_ANALYSIS_SOURCES,
					})}
				</p>
			</div>

			<div className="flex-1 overflow-y-auto px-1 py-1">
				<div className="space-y-2.5">
					{completedArtifacts.length > 0 ? (
						completedArtifacts.map((artifact, index) => {
							const isSelected = selectedFiles.some((f) => f.id === artifact.id)
							return (
								<div key={artifact.id} data-tour={index === 0 ? TOUR.modalPrimaryFirst : undefined}>
									<ArtifactCard
										artifact={artifact}
										isSelected={isSelected}
										mode="multi"
										disabled={!canAddMoreSources}
										onToggle={onToggle}
									/>
								</div>
							)
						})
					) : (
						<div className="text-sm text-muted-foreground text-center py-6">
							{t('pages.projectDetail.modal.noFilesAvailable')}
						</div>
					)}
				</div>
			</div>

			<UIButton
				type="button"
				onClick={onContinue}
				disabled={selectedFiles.length === 0}
				className="w-full"
			>
				{t('common.continue')}
			</UIButton>
		</div>
	)
}
